import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Grid, Box, Edit3, Tag, Shield, User, Users, Briefcase, LifeBuoy, Scale, RussianRuble } from 'lucide-react';
import api from '../config/api';
import SEO from '../components/SEO';
import './help.css';
import { BRAND } from '../config/brand';

const ICONS = {
  'registrazia': Box,
  'creation': Edit3,
  // Legacy sections removed from the domain:
  // boards, tags, pins
  'policy': Shield,
  'account': User,
  'community': Users,
  'uslugi': RussianRuble,
  'support': LifeBuoy,
  'legal': Scale
};

export default function HelpHome() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const { data, isLoading, error } = useQuery(
    ['help-index'],
    async () => {
      const res = await api.get('/api/pages/help-index');
      return Array.isArray(res?.data?.sections) ? res.data.sections : [];
    },
    { staleTime: 60 * 1000 }
  );

  const title = `Справка ${BRAND.name} — руководства и ответы`;
  const description = `Справка ${BRAND.name} — быстрые руководства и ответы на вопросы. Выберите раздел ниже или воспользуйтесь поиском.`;
  const canonical = `${BRAND.baseUrl}/help`;

  const sections = useMemo(() => data || [], [data]);

  return (
    <div className="min-h-screen bg-secondary-50">
      <SEO
        title={title}
        description={description}
        canonical={canonical}
        url={canonical}
        type="website"
        structuredData={[]}
        noindex={false}
      />

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Hero */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900 mb-3">Справка {BRAND.name}</h1>
          <p className="text-secondary-700">{description}</p>

          {/* Search */}
          <div className="mt-5 flex gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="input flex-1"
              placeholder="Поиск по справке…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const query = (q || '').trim();
                  if (query) navigate(`/search?q=${encodeURIComponent(query)}&scope=help`);
                }
              }}
            />
            <button
              className="btn btn-primary"
              onClick={() => {
                const query = (q || '').trim();
                if (query) navigate(`/search?q=${encodeURIComponent(query)}&scope=help`);
              }}
            >Искать</button>
          </div>
        </header>

        {/* Content */}
        {isLoading ? (
          <div className="p-8 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">Не удалось загрузить разделы</div>
        ) : sections.length === 0 ? (
          <div className="p-6 text-secondary-700">Пока нет опубликованных статей справки.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5">
            {sections.map((s) => {
              const Icon = ICONS[s.section] || BookOpen;
              return (
                <section key={s.section} className="relative bg-white border border-secondary-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="p-2 rounded-md bg-primary-600 text-white"><Icon className="w-5 h-5" /></div>
                    <div>
                      <h2 className="text-lg font-semibold text-secondary-900">{s.label}</h2>
                      <div className="text-xs text-secondary-500">{s.count} статей</div>
                    </div>
                  </div>
                  {/* Top links */}
                  {Array.isArray(s.top) && s.top.length > 0 && (
                    <ul className="list-disc ml-5 text-sm text-secondary-800 space-y-1 mb-3">
                      {s.top.map((p) => (
                        <li key={p.slug}>
                          <Link className="link" to={p.url}>{p.title}</Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="h-8">
                    <Link className="absolute bottom-4 right-4 text-sm text-primary-700 link" to={`/help/${encodeURIComponent(s.section)}`}>
                      Смотреть всё
                    </Link>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
